import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { StudyViewer, isValidUserId } from 'helper/flux/myStudyRow';

/** O aviso aparece nas 5 primeiras sessões; a partir da 6ª, some sem dispensa (D5). */
export const MIGRATION_NOTICE_MAX_SESSIONS = 5;

const STORAGE_PREFIX = 'flux-meus-estudos-aviso-casos-ampere-v1';

/**
 * Chave do aviso, uma por usuário e versionada: no localStorage guarda `{ sessions, dismissed }`;
 * no sessionStorage, o marcador de "sessão já contada".
 *
 * @param userId - Id do usuário logado.
 * @param prefix - Prefixo da chave, um por produto (padrão: o do Personalizado).
 */
export function migrationNoticeStorageKey(userId: number, prefix: string = STORAGE_PREFIX): string {
  return `${prefix}:${userId}`;
}

/** O que fica no localStorage: sessões já contadas e se o aviso foi dispensado. */
interface NoticeState {
  sessions: number;
  dismissed: boolean;
}

const FRESH_STATE: NoticeState = { sessions: 0, dismissed: false };

/**
 * Dispensas que não puderam ir para o localStorage (bloqueado ou cheio): valem até recarregar a
 * página, para o aviso fechado não voltar ao sair da aba e voltar.
 */
const dismissedInMemory = new Set<string>();

/** Lê o estado; qualquer falha (bloqueio, JSON inválido, formato errado) vale como "nada salvo". */
function readNoticeState(userId: number, prefix: string): NoticeState {
  try {
    const raw = window.localStorage.getItem(migrationNoticeStorageKey(userId, prefix));
    if (!raw) return FRESH_STATE;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return FRESH_STATE;
    const { sessions, dismissed } = value as Record<string, unknown>;
    return {
      // Só inteiro seguro > 0: fracionário, texto, negativo ou nulo valem como nenhuma sessão
      sessions: typeof sessions === 'number' && Number.isSafeInteger(sessions) && sessions > 0
        ? sessions : 0,
      dismissed: dismissed === true,
    };
  } catch {
    return FRESH_STATE;
  }
}

/** Grava o estado; devolve `false` se o localStorage não aceitou. */
function writeNoticeState(userId: number, state: NoticeState, prefix: string): boolean {
  try {
    window.localStorage.setItem(migrationNoticeStorageKey(userId, prefix), JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

/** Esta sessão do navegador já foi contada? `null` se o sessionStorage não puder ser lido. */
function isSessionCounted(userId: number, prefix: string): boolean | null {
  try {
    return window.sessionStorage.getItem(migrationNoticeStorageKey(userId, prefix)) !== null;
  } catch {
    return null;
  }
}

/** O aviso aparece nesta sessão? Leitura pura: pode rodar no render. */
function isNoticeVisible(userId: number, prefix: string): boolean {
  if (dismissedInMemory.has(`${prefix}:${userId}`)) return false;
  const { sessions, dismissed } = readNoticeState(userId, prefix);
  if (dismissed) return false;
  // A sessão atual, se ainda não contada, já entra na conta: a 6ª abre sem o aviso
  const current = isSessionCounted(userId, prefix) === false ? sessions + 1 : sessions;
  return current <= MIGRATION_NOTICE_MAX_SESSIONS;
}

/**
 * Conta a sessão atual, uma vez só: o marcador no sessionStorage segura o remonte da tela, o
 * recarregamento e o efeito duplo do StrictMode. Se o marcador não puder ser lido nem gravado,
 * não conta: o aviso fica até ser dispensado, em vez de sumir depois de 5 visitas à aba. O
 * contador para em 6.
 */
function registerSession(userId: number, prefix: string): void {
  if (isSessionCounted(userId, prefix) !== false) return;
  try {
    window.sessionStorage.setItem(migrationNoticeStorageKey(userId, prefix), '1');
  } catch {
    return;
  }
  const state = readNoticeState(userId, prefix);
  if (state.dismissed || state.sessions > MIGRATION_NOTICE_MAX_SESSIONS) return;
  writeNoticeState(userId, { ...state, sessions: state.sessions + 1 }, prefix);
}

export interface UseMigrationNoticeOptions extends StudyViewer {
  /** Prefixo da chave do aviso, um por produto (padrão: o do Personalizado). */
  storagePrefix?: string;
}

export interface UseMigrationNoticeResult {
  /** O aviso de "Casos Ampere" aparece agora. */
  visible: boolean;
  /** Dispensa de vez ("Entendi" ou "Mostrar casos Ampere"). */
  dismiss: () => void;
}

/**
 * Exibição do aviso de migração da caixa "Casos Ampere" (#1997, D5): só para admin com usuário
 * válido; some de vez ao ser dispensado ou, sem dispensa, a partir da 6ª sessão.
 *
 * Fica no localStorage por usuário (`flux-meus-estudos-aviso-casos-ampere-v1:<userId>`, valor
 * `{ sessions, dismissed }`), sempre com try/catch.
 *
 * Sessão = a sessão do navegador para a aba, a vida do sessionStorage (marcador na mesma chave):
 * - não conta de novo: recarregar a página, sair da aba Meus Estudos e voltar, o StrictMode;
 * - conta: abrir o site em outra aba ou reabrir o navegador. Cada aba nova é uma sessão (cinco
 *   abas abertas de uma vez gastam as cinco); uma aba restaurada ("reabrir aba fechada") recupera
 *   o sessionStorage e não conta;
 * - só conta quando o admin abre Meus Estudos (o hook montado), não a cada login;
 * - não há listener de `storage` entre abas: dispensar numa aba não fecha o aviso já aberto em
 *   outra, só na próxima vez que ela montar a tela.
 *
 * Storage indisponível, sem erro no console: com o localStorage bloqueado, o aviso aparece e a
 * dispensa vale até recarregar a página (memória do módulo); sem sessionStorage, a sessão não é
 * contada.
 *
 * @param viewer - Quem vê a tela (`selectUserId`/`selectUserIsAdmin`) e, opcional, o
 *   `storagePrefix` do produto. Com `userId` -1 (sem
 *   JWT), espera o login: o aviso aparece e a sessão é contada quando o id chega.
 * @returns Se o aviso aparece e a ação de dispensar.
 */
export default function useMigrationNotice({
  userId,
  isAdmin,
  storagePrefix = STORAGE_PREFIX,
}: UseMigrationNoticeOptions): UseMigrationNoticeResult {
  const active = isAdmin && isValidUserId(userId);
  // Recalcula quando o login ou o papel chegam depois do 1º render
  const visibleOnOpen = useMemo(
    () => active && isNoticeVisible(userId, storagePrefix),
    [active, userId, storagePrefix],
  );
  const [dismissedBy, setDismissedBy] = useState<number | null>(null);

  useEffect(() => {
    if (active) registerSession(userId, storagePrefix);
  }, [active, userId, storagePrefix]);

  const dismiss = useCallback(() => {
    if (!isValidUserId(userId)) return;
    const saved = writeNoticeState(
      userId,
      { ...readNoticeState(userId, storagePrefix), dismissed: true },
      storagePrefix,
    );
    if (!saved) dismissedInMemory.add(`${storagePrefix}:${userId}`);
    setDismissedBy(userId);
  }, [userId, storagePrefix]);

  // A dispensa desta tela vale para quem dispensou: trocar de usuário mostra o aviso do outro
  return { visible: visibleOnOpen && dismissedBy !== userId, dismiss };
}
